"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { playSound } from "@/lib/game/audio";
import { buildCountStarsRound } from "@/lib/game/countStars";
import { formatStarReward } from "@/lib/game/stars";
import { ActivityLayout } from "./ActivityLayout";
import { ActivitySuccessOverlay } from "./ActivitySuccessOverlay";
import { DifficultyPicker } from "./DifficultyPicker";
import { useGame } from "../GameProvider";

export function CountStarsActivity() {
  const { completeActivity, progress, setDifficulty, trackActivitySession } = useGame();
  const [tappedTargets, setTappedTargets] = useState<Set<string>>(new Set());
  const [roundKey, setRoundKey] = useState(0);
  const [feedback, setFeedback] = useState<"idle" | "wrong" | "right">("idle");
  const completedRef = useRef(false);
  const activityId = "count-stars";
  const difficulty = progress.difficulty;

  const round = useMemo(
    () => buildCountStarsRound(difficulty, roundKey),
    [difficulty, roundKey],
  );

  useEffect(() => {
    trackActivitySession(activityId);
  }, [trackActivitySession]);

  useEffect(() => {
    completedRef.current = false;
    setTappedTargets(new Set());
    setFeedback("idle");
  }, [difficulty, roundKey]);

  useEffect(() => {
    if (completedRef.current || feedback === "right") return;
    if (tappedTargets.size !== round.targetCount) {
      if (feedback === "wrong") setFeedback("idle");
      return;
    }

    completedRef.current = true;
    setFeedback("right");
    playSound("correct");
    completeActivity(activityId, difficulty);
  }, [
    tappedTargets,
    round.targetCount,
    feedback,
    activityId,
    difficulty,
    completeActivity,
  ]);

  const handleItemTap = (item: (typeof round.items)[number]) => {
    if (feedback === "right") return;

    if (!item.isTarget) {
      setFeedback("wrong");
      playSound("wrong");
      return;
    }

    setTappedTargets((prev) => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
  };

  const handlePlayAgain = () => {
    setTappedTargets(new Set());
    setFeedback("idle");
    completedRef.current = false;
    setRoundKey((k) => k + 1);
  };

  return (
    <ActivityLayout
      title="Count the Stars"
      headerExtra={
        <div className="flex items-center gap-2">
          <p className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Difficulty
          </p>
          <DifficultyPicker
            value={progress.difficulty}
            onChange={setDifficulty}
            compact
          />
        </div>
      }
    >
      <div
        key={`${difficulty}-${roundKey}`}
        className="relative flex h-full min-h-0 w-full flex-col overflow-hidden bg-gradient-to-b from-indigo-950 via-indigo-900 to-amber-50"
      >
        <div className="flex shrink-0 items-center justify-center gap-3 py-3">
          <span className="text-4xl font-black text-amber-100 sm:text-5xl">
            {round.targetCount}
          </span>
          <span className="text-5xl sm:text-6xl" aria-hidden>
            {round.targetSymbol}
          </span>
        </div>

        <div className="relative min-h-0 flex-1">
          <div className="absolute inset-0 px-3 py-2" role="group" aria-label="Sky symbols">
            {round.items.map((item) => {
              const selected = tappedTargets.has(item.id);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleItemTap(item)}
                  disabled={feedback === "right"}
                  aria-pressed={item.isTarget ? selected : undefined}
                  aria-label={item.symbol}
                  className={`absolute flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-2xl text-2xl transition sm:h-14 sm:w-14 sm:text-3xl disabled:opacity-90 ${
                    item.isTarget
                      ? selected
                        ? "scale-110 bg-yellow-300 shadow-lg ring-4 ring-yellow-400"
                        : "bg-white/95 shadow-md ring-2 ring-amber-200 active:scale-95"
                      : "bg-white/80 shadow-sm ring-1 ring-white/40 active:scale-95"
                  }`}
                  style={{ left: `${item.x * 100}%`, top: `${item.y * 100}%` }}
                >
                  {item.symbol}
                </button>
              );
            })}
          </div>

          {feedback === "wrong" && (
            <p className="pointer-events-none absolute bottom-3 left-0 right-0 text-center text-2xl font-black text-rose-200 drop-shadow">
              ✗
            </p>
          )}
        </div>

        <div className="shrink-0 border-t border-amber-200/40 bg-amber-50/95 px-4 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          <p className="text-center text-lg font-extrabold text-slate-700">
            {tappedTargets.size} / {round.targetCount}
          </p>
        </div>

        <ActivitySuccessOverlay
          show={feedback === "right"}
          title="Great counting!"
          theme="amber"
          rewardLabel={formatStarReward(difficulty)}
          onPlayAgain={handlePlayAgain}
        />
      </div>
    </ActivityLayout>
  );
}
